import React, { useState, useEffect } from 'react';
import {
  Modal, Box, Typography, TextField, Button, CircularProgress, FormControl, InputLabel, Select, MenuItem, Alert, IconButton, Grid
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useAuth } from '../../context/AuthContext';
import { createCharacter, getRaceOptions, getClassOptions } from '../../services/characterService';
import { v4 as uuidv4 } from 'uuid'; // For unique keys for dynamic inputs

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 500, md: 600 }, // Responsive width
  bgcolor: '#444444',
  border: '2px solid #d98d2a',
  boxShadow: 24,
  p: 4,
  color: '#F5F5F5',
  fontFamily: '"Georgia", serif',
  maxHeight: '90vh',
  overflowY: 'auto'
};

const CharacterCreationModal = ({ open, handleClose, onCharacterCreated }) => {
  const { firestoreUser } = useAuth();  const [name, setName] = useState('');
  const [race, setRace] = useState('');
  const [dndBeyondLink, setDndBeyondLink] = useState(''); // Added DNDBeyond link field
  // Initialize with one class entry
  const [selectedClasses, setSelectedClasses] = useState([{ id: uuidv4(), className: '', classLevel: 1 }]);
  const [characterImage, setCharacterImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [races, setRaces] = useState([]);
  const [classOptions, setClassOptions] = useState([]); // Renamed from 'classes' to avoid confusion
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;

    const fetchOptions = async () => {
      setLoadingOptions(true);
      setError('');
      try {
        const [raceOpts, classOptsData] = await Promise.all([getRaceOptions(), getClassOptions()]);
        if (active) {
          setRaces(raceOpts || []);
          setClassOptions(classOptsData || []); 
          if ((raceOpts || []).length === 0) console.warn("No race options found.");
          if ((classOptsData || []).length === 0) console.warn("No class options found.");
        }
      } catch (err) {
        console.error("Failed to fetch character options:", err);
        if (active) {
          setError("Failed to load character creation options. Please try again.");
        }
      } finally {
        if (active) {
          setLoadingOptions(false);
        }
      }
    };

    if (open) {
      fetchOptions();
    }

    return () => {
      active = false;
    };
  }, [open]);

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Image size should not exceed 5MB.');
        return;
      }
      setCharacterImage(file);
      setImagePreview(URL.createObjectURL(file));
      setError('');
    } else {
      setCharacterImage(null);
      setImagePreview('');
    }
  };

  const handleAddClass = () => {
    if (selectedClasses.length < 5) {
      setSelectedClasses([...selectedClasses, { id: uuidv4(), className: '', classLevel: 1 }]);
    }
  };

  const handleRemoveClass = (id) => {
    if (selectedClasses.length > 1) {
      setSelectedClasses(selectedClasses.filter(cls => cls.id !== id));
    }
  };

  const handleClassChange = (id, field, value) => {
    setSelectedClasses(selectedClasses.map(cls =>
      cls.id === id ? { ...cls, [field]: field === 'classLevel' ? Math.max(1, parseInt(value, 10) || 1) : value } : cls
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firestoreUser || !firestoreUser.uid) {
      setError("User not logged in properly.");
      return;
    }
    if (!name || !race) {
      setError("Please fill in Name and Race.");
      return;
    }
    const validClasses = selectedClasses.filter(cls => cls.className && cls.classLevel >= 1);
    if (validClasses.length === 0) {
      setError("Please add at least one valid class with a level.");
      return;
    }
    if (validClasses.length !== selectedClasses.length) {
        setError("Please ensure all added classes have a name and a level of at least 1.");
        return;
    }

    setSubmitting(true);
    setError('');
    try {      const characterData = {
        name,
        userId: firestoreUser.uid,
        race,
        classes: validClasses.map(({ id, ...rest }) => rest), // Submit only className and classLevel, remove id
        imageFile: characterImage,
        dndBeyondLink: dndBeyondLink.trim() || null, // Include D&D Beyond link
      };
      await createCharacter(characterData);
      onCharacterCreated();
      handleModalClose();
    } catch (err) {
      console.error("Failed to create character:", err);
      setError(err.message || "Failed to create character. Please try again.");
    }
    setSubmitting(false);
  };
  const resetForm = () => {
    setName('');
    setRace('');
    setDndBeyondLink(''); // Reset D&D Beyond link
    setSelectedClasses([{ id: uuidv4(), className: '', classLevel: 1 }]);
    setCharacterImage(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview('');
    setError('');
  }

  const handleModalClose = () => {
    resetForm();
    handleClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="create-character-modal-title"
    >
      <Box sx={modalStyle} component="form" onSubmit={handleSubmit}>
        <Typography id="create-character-modal-title" variant="h6" component="h2" sx={{ color: '#d98d2a', fontFamily: 'inherit', mb: 2 }}>
          Forge Your Legend
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2, bgcolor: '#ff6b6b', color: 'white' }}>{error}</Alert>}        <TextField
          label="Character Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          margin="normal"
          InputLabelProps={{ style: { color: '#d98d2a', fontFamily: 'inherit' } }}
          inputProps={{ style: { color: '#F5F5F5', fontFamily: 'inherit' } }}
          sx={{ mb: 1, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d98d2a' } } }}
        />

        <TextField
          label="D&D Beyond Character Link (Optional)"
          value={dndBeyondLink}
          onChange={(e) => setDndBeyondLink(e.target.value)}
          fullWidth
          type="url"
          margin="normal"
          placeholder="https://www.dndbeyond.com/characters/..."
          InputLabelProps={{ style: { color: '#d98d2a', fontFamily: 'inherit' } }}
          inputProps={{ style: { color: '#F5F5F5', fontFamily: 'inherit' } }}
          sx={{ mb: 1, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d98d2a' } } }}
        />

        {loadingOptions ? <CircularProgress sx={{color: '#d98d2a', display: 'block', margin: 'auto', my: 2 }} /> : (
          <>
            <FormControl fullWidth margin="normal" sx={{ mb: 1 }}>
              <InputLabel id="race-select-label" sx={{ color: '#d98d2a', fontFamily: 'inherit' }}>Race</InputLabel>
              <Select
                labelId="race-select-label"
                value={race}
                label="Race"
                onChange={(e) => setRace(e.target.value)}
                required
                sx={{ color: '#F5F5F5', fontFamily: 'inherit', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' }, '& .MuiSvgIcon-root': { color: '#d98d2a' } }}
              >
                {races.map((r) => <MenuItem key={r} value={r} sx={{fontFamily: 'inherit'}}>{r}</MenuItem>)}
              </Select>
            </FormControl>

            <Typography variant="subtitle1" sx={{ color: '#d98d2a', fontFamily: 'inherit', mt: 2, mb: 1 }}>
              Classes (up to 5)
            </Typography>
            {selectedClasses.map((cls, index) => (
              <Grid container spacing={1} key={cls.id} alignItems="center" sx={{ mb: 1 }}>
                <Grid item xs={6} sm={7}>
                  <FormControl fullWidth>
                    <InputLabel id={`class-name-label-${cls.id}`} sx={{ color: '#d98d2a', fontFamily: 'inherit' }}>Class #{index + 1}</InputLabel>
                    <Select
                      labelId={`class-name-label-${cls.id}`}
                      value={cls.className}
                      label={`Class #${index + 1}`}
                      onChange={(e) => handleClassChange(cls.id, 'className', e.target.value)}
                      required
                      sx={{ color: '#F5F5F5', fontFamily: 'inherit', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' }, '& .MuiSvgIcon-root': { color: '#d98d2a' } }}
                    >
                      {classOptions.map((cOpt) => <MenuItem key={cOpt} value={cOpt} sx={{fontFamily: 'inherit'}}>{cOpt}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4} sm={3}>
                  <TextField
                    label="Level"
                    type="number"
                    value={cls.classLevel}
                    onChange={(e) => handleClassChange(cls.id, 'classLevel', e.target.value)}
                    required
                    fullWidth
                    InputLabelProps={{ style: { color: '#d98d2a', fontFamily: 'inherit' } }}
                    inputProps={{ style: { color: '#F5F5F5', fontFamily: 'inherit', min: 1 } }}
                    sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d98d2a' } } }}
                  />
                </Grid>
                <Grid item xs={2} sm={2} sx={{textAlign: "right"}}>
                  {selectedClasses.length > 1 && (
                    <IconButton onClick={() => handleRemoveClass(cls.id)} sx={{color: '#d98d2a'}}>
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            ))}
            {selectedClasses.length < 5 && (
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddClass}
                fullWidth
                variant="outlined"
                sx={{ 
                    color: '#d98d2a', borderColor: '#d98d2a', fontFamily: 'inherit', mt: 1,
                    '&:hover': {borderColor: '#c87d26', backgroundColor: 'rgba(217, 141, 42, 0.1)'}
                }}
              >
                Add Another Class
              </Button>
            )}
          </>
        )}

        <Typography variant="subtitle1" sx={{ color: '#d98d2a', fontFamily: 'inherit', mt: 2, mb: 0.5 }}>
          Character Image (Optional)
        </Typography>
        <input
          accept="image/*"
          style={{ display: 'none' }}
          id="character-image-upload"
          type="file"
          onChange={handleImageChange}
        />
        <label htmlFor="character-image-upload">
          <Button variant="outlined" component="span" fullWidth sx={{
            color: '#d98d2a', 
            borderColor: '#d98d2a', 
            fontFamily: 'inherit', 
            mb: imagePreview ? 1 : 2,
            '&:hover': {borderColor: '#c87d26', backgroundColor: 'rgba(217, 141, 42, 0.1)'}
          }}>
            Upload Image
          </Button>
        </label>
        {imagePreview && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <img src={imagePreview} alt="Character Preview" style={{ maxHeight: '150px', maxWidth: '100%', borderRadius: '4px' }} />
          </Box>
        )}

        <Button type="submit" variant="contained" disabled={submitting || loadingOptions} sx={{ 
          fontFamily: 'inherit',
          backgroundColor: '#d98d2a', 
          color: '#1c1c1c',
          '&:hover': { backgroundColor: '#c87d26' },
          mr: 1, mt: 2
        }}>
          {submitting ? <CircularProgress size={24} sx={{color: '#1c1c1c'}} /> : "Forge Character"}
        </Button>
        <Button onClick={handleModalClose} sx={{ color: '#F5F5F5', fontFamily: 'inherit', mt:2 }}>Cancel</Button>
      </Box>
    </Modal>
  );
};

export default CharacterCreationModal;
